import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Check, X, UserPlus, ChevronDown, ChevronUp, FileText, AlertTriangle, Landmark } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// A row is on SEPA Direct Debit if any of these markers are set — the FA
// still needs to lodge the mandate with the bank offline before the
// membership can go live, and we surface a very loud visual cue on the card.
const isSepaRow = (m) =>
  m?.payment_status === "sepa"
  || m?.subscription_mode === "sepa"
  || m?.payment_method === "sepa"
  || !!m?.sepa_mandate_url
  || !!m?.sepa_mandate_storage_path
  || !!m?.sepa_mandate_pdf;

export default function PendingApprovals({ onCountChange }) {
  const [pending, setPending] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type, memberId, name }
  const [sepaAckBusy, setSepaAckBusy] = useState(null);
  const { authAxios } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendRes, cancelRes] = await Promise.all([
        authAxios.get("/members/pending-approvals"),
        authAxios.get("/members/cancellation-requests"),
      ]);
      setPending(pendRes.data);
      setCancellations(cancelRes.data);
      onCountChange?.(pendRes.data.length + cancelRes.data.length);
    } catch { toast.error("Failed to load approvals"); }
    finally { setLoading(false); }
  };

  const [approvalErrors, setApprovalErrors] = useState([]);

  const handleApprove = async (memberId, memberName) => {
    try { await authAxios.post(`/members/${memberId}/approve`); toast.success("Member approved"); fetchData(); }
    catch (e) {
      const detail = e.response?.data?.detail || "Error";
      // Persistent red banner for Stripe-charge failures on approval so the
      // FA sees exactly why the approval bounced and can chase the member.
      if (/failed to charge/i.test(detail) || /card_declined/i.test(detail) || /stripe/i.test(detail)) {
        setApprovalErrors((prev) => [
          { id: `${memberId}_${Date.now()}`, memberId, memberName, message: detail },
          ...prev,
        ]);
      } else {
        toast.error(detail);
      }
    }
  };

  const handleReject = async (memberId) => {
    try { await authAxios.post(`/members/${memberId}/reject`); toast.success("Registration rejected"); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleSepaAckToggle = async (memberId, nextRegistered) => {
    setSepaAckBusy(memberId);
    // Optimistic update so the Approve gate flips immediately.
    setPending((prev) => prev.map((m) => m.member_id === memberId
      ? { ...m, sepa_mandate_registered_at: nextRegistered ? new Date().toISOString() : null }
      : m));
    try {
      await authAxios.post(`/members/${memberId}/sepa-mandate/register`, { registered: nextRegistered });
      toast.success(nextRegistered ? "SEPA mandate marked as registered" : "SEPA acknowledgement cleared");
      fetchData();
    } catch (e) {
      // Revert optimistic on failure.
      setPending((prev) => prev.map((m) => m.member_id === memberId
        ? { ...m, sepa_mandate_registered_at: nextRegistered ? null : m.sepa_mandate_registered_at }
        : m));
      toast.error(e.response?.data?.detail || "Failed to update SEPA acknowledgement");
    } finally { setSepaAckBusy(null); }
  };

  const handleApproveCancellation = async (memberId) => {
    try {
      const res = await authAxios.post(`/members/${memberId}/approve-cancellation`);
      // Surface what actually happened: Stripe outcome + convert-vs-deactivate.
      const d = res.data || {};
      const conv = d.conversion && d.conversion.converted;
      const bal = typeof d.primary_balance === "number" ? d.primary_balance : null;
      const stripe = d.stripe && d.stripe[memberId];
      let msg = conv
        ? `Cancellation approved — converted to Guest${bal ? ` (€${bal.toFixed(2)} preserved)` : ""}`
        : "Cancellation approved — member deactivated";
      if (stripe && stripe.stripe_status === "cancelled") msg += " · Stripe subscription stopped";
      else if (stripe && stripe.stripe_status === "failed") msg += " · Stripe cancel FAILED — check dashboard";
      toast.success(msg);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleRejectCancellation = async (memberId) => {
    try { await authAxios.post(`/members/${memberId}/reject-cancellation`); toast.success("Cancellation rejected"); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const executeConfirm = () => {
    if (!confirmAction) return;
    const { type, memberId } = confirmAction;
    setConfirmAction(null);
    if (type === "reject") handleReject(memberId);
    else if (type === "approve-cancel") handleApproveCancellation(memberId);
    else if (type === "reject-cancel") handleRejectCancellation(memberId);
  };

  const confirmMessages = {
    "reject": { title: "Reject Registration", desc: "Are you sure you want to reject this registration? This action cannot be undone." },
    "approve-cancel": { title: "Approve Cancellation", desc: "This will cancel the membership. Linked child members will also be cancelled. This action cannot be undone." },
    "reject-cancel": { title: "Reject Cancellation", desc: "Are you sure you want to reject this cancellation request?" },
  };

  const downloadSepaPdf = async (memberId) => {
    try {
      const res = await fetch(`${API}/wallet/sepa-mandate/${memberId}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Failed to download SEPA mandate"); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div data-testid="pending-approvals">
      {/* Persistent red banner for approval-time Stripe failures. */}
      {approvalErrors.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="approval-error-banner">
          {approvalErrors.map((err) => (
            <div key={err.id} className="rounded-2xl border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center font-bold">!</div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-red-800 text-sm">
                  Stripe declined the card on approval of {err.memberName || err.memberId}
                </p>
                <p className="text-red-700 text-sm mt-1">
                  Ask the member to update their payment method, then re-approve.
                </p>
                <p className="text-red-600 text-xs mt-2 font-mono break-words">{err.message}</p>
              </div>
              <button
                onClick={() => setApprovalErrors((prev) => prev.filter((e) => e.id !== err.id))}
                className="flex-shrink-0 text-red-500 hover:text-red-800 font-bold text-lg leading-none"
                data-testid={`dismiss-approval-error-${err.memberId}`}
                aria-label="Dismiss"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Registration Approvals */}
      <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">New Registration Requests ({pending.length})</h2>
      {pending.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm mb-8"><UserPlus className="w-12 h-12 text-slate-300 mx-auto mb-2" /><p className="text-slate-500">No pending registrations</p></div>
      ) : (
        <div className="space-y-3 mb-8">
          {pending.map(m => {
            const sepa = isSepaRow(m);
            const sepaRegistered = !!m.sepa_mandate_registered_at;
            const approveDisabled = sepa && !sepaRegistered;
            return (
            <div key={m.member_id} className={`bg-white rounded-2xl p-4 shadow-sm ${sepa ? "ring-2 ring-amber-300" : ""}`} data-testid={`pending-row-${m.member_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><UserPlus className="w-5 h-5 text-amber-600" /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-[#1e3a5f] truncate">{m.name}</p>
                      {sepa && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide"
                          data-testid={`sepa-badge-${m.member_id}`}
                        >
                          <Landmark className="w-3 h-3" /> SEPA
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-sm truncate">{m.email}</p>
                  </div>
                </div>
                <button onClick={() => setExpanded(expanded === m.member_id ? null : m.member_id)} className="text-slate-400">{expanded === m.member_id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}</button>
              </div>

              {/* SEPA mandate banner — always visible on SEPA rows, gates Approve. */}
              {sepa && (
                <div
                  className={`rounded-xl border-2 p-3 mb-3 ${sepaRegistered ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}
                  data-testid={`sepa-banner-${m.member_id}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${sepaRegistered ? "text-green-600" : "text-amber-600"}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-sm ${sepaRegistered ? "text-green-800" : "text-amber-900"}`}>
                        {sepaRegistered ? "SEPA mandate registered with the bank" : "SEPA Direct Debit — register mandate with the bank before approving"}
                      </p>
                      {sepaRegistered ? (
                        <p className="text-green-700 text-xs mt-1">
                          Registered {new Date(m.sepa_mandate_registered_at).toLocaleString()}
                          {m.sepa_mandate_registered_by ? ` by ${m.sepa_mandate_registered_by}` : ""}.
                        </p>
                      ) : (
                        <p className="text-amber-800 text-xs mt-1">
                          Download the signed mandate PDF, lodge it with your bank, then tick the box below. Approve stays locked until this is done.
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => downloadSepaPdf(m.member_id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-amber-300 text-amber-800 text-xs font-medium hover:bg-amber-100"
                          data-testid={`sepa-download-${m.member_id}`}
                        >
                          <FileText className="w-3 h-3" /> Download Mandate PDF
                        </button>
                      </div>
                      <label className="mt-2 flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={sepaRegistered}
                          disabled={sepaAckBusy === m.member_id}
                          onChange={(e) => handleSepaAckToggle(m.member_id, e.target.checked)}
                          className="w-4 h-4 rounded border-amber-400 accent-amber-600"
                          data-testid={`sepa-ack-${m.member_id}`}
                        />
                        <span className={`text-xs font-semibold ${sepaRegistered ? "text-green-800" : "text-amber-900"}`}>
                          I've registered this mandate with the bank
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {expanded === m.member_id && (
                <div className="bg-slate-50 rounded-xl p-3 mb-3 grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-slate-500 text-xs">DOB</p><p className="font-medium">{m.dob}</p></div>
                  <div><p className="text-slate-500 text-xs">Type</p><p className="font-medium">{m.member_type_name || "—"}</p></div>
                  <div className="col-span-2"><p className="text-slate-500 text-xs">Address</p><p className="font-medium">{m.address || "—"}</p></div>
                  {m.mobile_number && <div><p className="text-slate-500 text-xs">Mobile</p><p className="font-medium">{m.mobile_number}</p></div>}
                  <div><p className="text-slate-500 text-xs">Annual Fee</p><p className="font-bold text-green-600">{"\u20ac"}{(m.annual_fee || 0).toFixed(2)}</p></div>
                  <div><p className="text-slate-500 text-xs">Payment</p><p className="font-medium">{m.payment_status === "sepa" ? "SEPA Direct Debit" : m.payment_status === "paid" ? "Stripe" : m.payment_status === "wallet_paid" ? "Wallet" : m.payment_status || "—"}</p></div>
                  <div><p className="text-slate-500 text-xs">Registered</p><p className="font-medium">{new Date(m.created_at).toLocaleDateString()}</p></div>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => approveDisabled
                    ? toast.error("Tick the SEPA mandate acknowledgement before approving")
                    : handleApprove(m.member_id, m.name)}
                  disabled={approveDisabled}
                  className="flex-1 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`approve-${m.member_id}`}
                  title={approveDisabled ? "Register SEPA mandate first" : "Approve member"}
                ><Check className="w-4 h-4" /> Approve</button>
                <button onClick={() => setConfirmAction({ type: "reject", memberId: m.member_id, name: m.name })} className="flex-1 py-2 rounded-xl bg-red-500 text-white font-bold flex items-center justify-center gap-1" data-testid={`reject-${m.member_id}`}><X className="w-4 h-4" /> Reject</button>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Cancellation Requests */}
      <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">Cancellation Requests ({cancellations.length})</h2>
      {cancellations.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm mb-8"><p className="text-slate-500">No cancellation requests</p></div>
      ) : (
        <div className="space-y-3 mb-8">
          {cancellations.map(m => (
            <div key={m.member_id} className="bg-white rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div><p className="font-bold text-[#1e3a5f]">{m.name}</p><p className="text-slate-500 text-sm">{m.email} | {m.member_id}</p></div>
                <span className="text-xs text-amber-500 font-bold">CANCEL REQUEST</span>
              </div>
              <p className="text-sm text-red-500 mb-3">Approving will cancel this membership. Linked child members will also be cancelled.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmAction({ type: "approve-cancel", memberId: m.member_id, name: m.name })} className="flex-1 py-2 rounded-xl bg-red-500 text-white font-bold flex items-center justify-center gap-1" data-testid={`cancel-approve-${m.member_id}`}><Check className="w-4 h-4" /> Approve</button>
                <button onClick={() => setConfirmAction({ type: "reject-cancel", memberId: m.member_id, name: m.name })} className="flex-1 py-2 rounded-xl bg-slate-500 text-white font-bold flex items-center justify-center gap-1" data-testid={`cancel-reject-${m.member_id}`}><X className="w-4 h-4" /> Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction ? confirmMessages[confirmAction.type]?.title : ""}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction ? `${confirmMessages[confirmAction.type]?.desc} (${confirmAction.name})` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeConfirm} data-testid="confirm-action-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
